import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Github,
  Folder,
  Mail,
  Check,
  X,
  ExternalLink,
  Loader2,
  ArrowUpDown,
  Settings,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { fetchProjects, declineInvitation } from '@/api/projects';
import type { Project, PendingInvitation } from '@/types/project';
import { formatPlanLabel } from '@/lib/billing';

type SortOption = 'name-asc' | 'name-desc' | 'version-desc' | 'version-asc';

export function ProjectsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('name-asc');
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [onboardingWizardDismissed, setOnboardingWizardDismissed] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null);
  
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle GitHub connection success
  useEffect(() => {
    if (searchParams.get('github_connected') === 'true') {
      toast.success('GitHub account connected successfully!', {
        description: 'You can now create projects from your GitHub repositories.',
      });
      // Remove the query parameter - use new URLSearchParams to avoid mutation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('github_connected');
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
  });

  // Auto-open onboarding wizard if needed (only once per session)
  useEffect(() => {
    if (data?.show_onboarding_wizard && !createProjectDialogOpen && !onboardingWizardDismissed) {
      setCreateProjectDialogOpen(true);
    }
  }, [data?.show_onboarding_wizard, createProjectDialogOpen, onboardingWizardDismissed]);

  const declineMutation = useMutation({
    mutationFn: (invitationId: string) => declineInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setDeclineDialogOpen(false);
      setSelectedInvitation(null);
    },
  });

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    
    let filtered = data.projects.filter((project) => {
      const query = searchQuery.toLowerCase();
      return (
        project.name.toLowerCase().includes(query) ||
        (project.github_repo?.toLowerCase().includes(query) ?? false) ||
        (project.odoo_version?.toLowerCase().includes(query) ?? false)
      );
    });

    // Sort projects
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'version-desc':
          return (b.odoo_version ?? '').localeCompare(a.odoo_version ?? '');
        case 'version-asc':
          return (a.odoo_version ?? '').localeCompare(b.odoo_version ?? '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [data?.projects, searchQuery, sortOption]);

  const handleDeclineClick = (invitation: PendingInvitation) => {
    setSelectedInvitation(invitation);
    setDeclineDialogOpen(true);
  };

  const handleDeclineConfirm = () => {
    if (selectedInvitation) {
      declineMutation.mutate(selectedInvitation.id);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-red-500">Failed to load projects. Please try again.</p>
        </div>
      </DashboardLayout>
    );
  }

  const { projects, pending_invitations, has_github } = data || { 
    projects: [], 
    pending_invitations: [], 
    has_github: false 
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto w-full px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-muted-foreground mt-1">
              Manage and launch your Odoo projects
            </p>
          </div>
          <div>
            {has_github ? (
              <Button onClick={() => setCreateProjectDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            ) : (
              <Button variant="outline" asChild>
                <a href="/api/v1/github_app/install">
                  <Github className="h-4 w-4 mr-2" />
                  Link GitHub to Create Project
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Search and Sort Toolbar */}
        {(projects.length > 0 || pending_invitations.length > 0) && (
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search projects by name, repo, or version"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <div className="flex items-center gap-3">
              <Select value={sortOption} onValueChange={(v) => setSortOption(v as SortOption)}>
                <SelectTrigger className="w-[200px] h-11">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">Name A → Z</SelectItem>
                  <SelectItem value="name-desc">Name Z → A</SelectItem>
                  <SelectItem value="version-desc">Version High → Low</SelectItem>
                  <SelectItem value="version-asc">Version Low → High</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {pending_invitations.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Pending Invitations
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {pending_invitations.map((invitation) => (
                <Card key={invitation.id} className="h-full border border-primary/40 shadow-sm">
                  <CardContent className="p-5 flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-lg truncate leading-snug" title={invitation.project_name}>
                        {invitation.project_name}
                      </h3>
                      <Badge variant="default" className="bg-primary text-white">
                        Invited
                      </Badge>
                    </div>

                    <Badge variant="secondary" className="w-fit capitalize bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {invitation.role}
                    </Badge>
                    {invitation.invited_by && (
                      <p className="text-sm text-muted-foreground">
                        Invited by {invitation.invited_by}
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        size="sm" 
                        className="w-full"
                        asChild
                      >
                        <a href={`/project-invite/${invitation.id}/join`}>
                          <Check className="h-4 w-4 mr-1" />
                          Accept
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDeclineClick(invitation)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {projects.length === 0 && pending_invitations.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Folder className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">No projects yet</h2>
              <p className="text-muted-foreground mb-6">
                Create your first project to get started
              </p>
              {has_github ? (
                <Button onClick={() => setCreateProjectDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              ) : (
                <Button variant="outline" asChild>
                  <a href="/api/v1/github_app/install">
                    <Github className="h-4 w-4 mr-2" />
                    Link GitHub to Create Project
                  </a>
                </Button>
              )}
              <div className="mt-6 pt-4 border-t text-sm text-muted-foreground">
                Need help? Check out our{' '}
                <a 
                  href="https://docs.doploy.io/getting-started/creating-project" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  project creation guide
                </a>{' '}
                or contact{' '}
                <a href="mailto:support@doploy.io" className="text-primary hover:underline">
                  support@doploy.io
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Projects Grid */}
        {filteredProjects.length > 0 && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
            
            {/* Create New Project Card */}
            <CreateProjectCard
              hasGitHub={has_github}
              onCreateProject={() => setCreateProjectDialogOpen(true)}
            />
          </div>
        )}

        {/* No results from search */}
        {projects.length > 0 && filteredProjects.length === 0 && searchQuery && (
          <Card>
            <CardContent className="py-8 text-center">
              <Search className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">
                No projects match "{searchQuery}"
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Decline Invitation Dialog */}
      <AlertDialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to decline the invitation to join "{selectedInvitation?.project_name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeclineConfirm}
              disabled={declineMutation.isPending}
            >
              {declineMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                'Decline'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={createProjectDialogOpen}
        onOpenChange={(open) => {
          setCreateProjectDialogOpen(open);
          if (!open) setOnboardingWizardDismissed(true);
        }}
        isFirstProject={data?.is_first_project}
      />
    </DashboardLayout>
  );
}

function ProjectCard({ project }: { project: Project }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const planLabel = project.billing_plan ? formatPlanLabel(project.billing_plan) : null;

  const handleOpenProject = () => {
    setIsLoading(true);
    navigate(`/project/${project.id}/deployments`);
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/project/${project.id}/settings`);
  };

  const handleGitHubClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (project.github_repo) {
    window.open(`https://github.com/${project.github_repo}`, '_blank');
    }
  };

  return (
    <Card 
      className="group h-full flex flex-col gap-0 py-0 border border-gray-200/90 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all bg-white overflow-hidden"
      onClick={handleOpenProject}
    >
      <CardContent className="p-4 flex-1 flex flex-col gap-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate" title={project.name}>
              {project.name}
            </h3>
            <p className="text-xs text-muted-foreground truncate">
              {project.github_repo || 'Project workspace'}
            </p>
          </div>
          {planLabel && (
            <Badge variant="outline" className="capitalize border-primary/30 text-primary bg-primary/5">
              {planLabel}
            </Badge>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border border-indigo-100">
              Odoo {project.odoo_version || '—'}
            </Badge>
            {project.has_active_subscription && (
              <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50">
                Active subscription
              </Badge>
            )}
          </div>
          
          <div
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
              project.github_repo
                ? 'cursor-pointer hover:border-primary/60 hover:bg-primary/5'
                : 'text-muted-foreground bg-muted/50 border-dashed'
            }`}
            onClick={project.github_repo ? handleGitHubClick : undefined}
            >
            <Github className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {project.github_repo || 'No GitHub repo linked'}
            </span>
            {project.github_repo && <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />}
          </div>
        </div>

        {!project.has_active_subscription && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>No active subscription. Set up billing to deploy.</span>
          </div>
        )}
        {project.is_subscription_ending && project.has_active_subscription && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>Subscription ending soon.</span>
          </div>
        )}

        <div className="mt-auto pt-0">
          <Button 
            className="w-full" 
            disabled={isLoading}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenProject();
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Opening...
              </>
            ) : (
              'Open Project'
            )}
          </Button>
        </div>
      </CardContent>

      <CardFooter className="px-4 py-2 bg-gradient-to-r from-gray-50 via-gray-50 to-gray-100 border-t flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={handleSettingsClick}
        >
          <Settings className="h-4 w-4 mr-1" />
          Settings
        </Button>
        <Button 
          variant="link" 
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/project/${project.id}/deployments`);
          }}
        >
          Dashboard
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </CardFooter>
    </Card>
  );
}

function CreateProjectCard({ hasGitHub, onCreateProject }: { hasGitHub: boolean; onCreateProject: () => void }) {
  return (
    <Card className="h-full flex flex-col gap-0 py-0 border border-dashed border-gray-300 bg-white shadow-sm">
      <CardContent className="p-3.5 flex-1 flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Plus className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg leading-tight">Create a New Project</h3>
            <p className="text-sm text-muted-foreground">Spin up a new Odoo workspace.</p>
          </div>
        </div>
        
        {hasGitHub ? (
          <>
            <p className="text-sm text-muted-foreground">
              Link your repo and we’ll handle automated deployments.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button className="sm:w-auto w-full" onClick={onCreateProject}>
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </Button>
            </div>
          </>
        ) : (
          <div className="space-y-2.5 text-sm">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
                <div className="space-y-1.5 text-amber-800">
                  <p className="font-semibold leading-tight">GitHub connection required</p>
                  <p className="leading-snug">
                    Link GitHub to:
                </p>
                  <ul className="list-disc list-inside space-y-1 text-[13px]">
                  <li>Access your repositories</li>
                  <li>Set up automated deployments</li>
                </ul>
                </div>
              </div>
            </div>
            <Button variant="outline" className="w-full" asChild>
              <a href="/api/v1/github_app/install">
                <Github className="h-4 w-4 mr-2" />
                Link GitHub Account
              </a>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
